import { QueryResult } from '@apollo/client';
import { ExtendedUser } from '../types';
import { useEffect, useCallback, useRef } from 'react';
import { useAuthToken } from './useAuthToken';
import { useMeLazyQuery, MeQuery, Exact } from '@src/api/graphql-codegen';
import { isHomeEDApp } from '@constants';
import { initUserSettingsFromUserData } from './useUserSettings';
import { logger } from '../utils/logger';

type UseUserResponse = [ExtendedUser | undefined, QueryResult<MeQuery, Exact<{
  [key: string]: never;
}>>];
export const useUser = (forceUpdate?: boolean): UseUserResponse => {
  const { token } = useAuthToken();
  const [getUser, query] = useMeLazyQuery({
    fetchPolicy: forceUpdate ? 'network-only' : 'cache-first',
    nextFetchPolicy: 'cache-first'
  });

  // Use ref to store the getUser function to avoid recreating fetchUser constantly
  const getUserRef = useRef(getUser);
  getUserRef.current = getUser;

  const fetchUser = useCallback(async () => {
    try {
      if (token) {
        await getUserRef.current();
      }
    } catch (e) {
      logger.info('fetching user error - ', e)
    }
  }, [token]); // Remove getUser from dependencies to prevent infinite re-renders

  useEffect(() => {
    if (token && !isHomeEDApp && !query?.loading) {
      fetchUser();
    }
  }, [token, fetchUser]); // Keep fetchUser dependency since it's now stable
  
  // Initialize user settings when user data is loaded
  useEffect(() => {
    if (query?.data?.me) {
      // Initialize user settings from the loaded user data
      initUserSettingsFromUserData(query.data.me);
    }
  }, [query?.data?.me?.id]); // Use user ID instead of entire user object to prevent constant re-runs

  return [query?.data?.me || undefined, query];
};
